import { useEffect } from 'react';
import { peek, prefetch, run } from './queryCache';
import { queries } from './queries';
import type { Query } from './queries';
import { rememberedEnvironment, resolveEnvironment } from './pages/project/environmentSelection';
import type { Environment } from './api';

/**
 * Warm what a link leads to as soon as someone shows intent (hover, focus,
 * touch), so by the click the page's data is already there or on its way.
 * It asks for exactly what the destination page asks for (queries.ts), a
 * fresh answer is never asked for again, and nothing is changed: reads only.
 */

const INTENT_DELAY = 65;

const warm = (query: Query<unknown>) => prefetch(query.key, query.load);

async function warmProject(projectId: string, section: string | undefined, wantedEnvironment: string | null): Promise<void> {
  const project = queries.project(projectId);
  warm(queries.servers());
  warm(queries.applications(projectId));
  warm(queries.historyApplications(projectId));
  warm(queries.incidents(projectId));
  if (section === 'monitoring') warm(queries.monitoring(projectId));
  // What is read per environment depends on which environment the page opens
  // on, which the project itself says.
  await run(project.key, project.load);
  const record = peek<{ environments?: Environment[] }>(project.key).data;
  const environment = resolveEnvironment(record?.environments ?? [], wantedEnvironment ?? rememberedEnvironment(projectId));
  if (!environment) return;
  const id = environment.id;
  warm(queries.environmentOverview(projectId, id));
  warm(queries.components(projectId, id));
  warm(queries.deployments(projectId, id));
  warm(queries.recoveryPoints(projectId, id));
  warm(queries.plan(projectId, id));
  warm(queries.environmentHealth(projectId, id));
}

/** Warm the data of an in-app destination. Anything it does not recognise is left alone. */
export function prefetchPath(href: string): void {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return;
  }
  if (url.origin !== window.location.origin) return;
  const path = url.pathname.replace(/\/+$/, '') || '/';
  if (path === '/app') {
    warm(queries.overview());
    warm(queries.organizationHealth());
  } else if (path === '/app/projects') {
    warm(queries.projects());
    warm(queries.organizationHealth());
  } else if (path === '/app/servers') {
    warm(queries.serverRows());
    warm(queries.organizationHealth());
  } else if (path === '/app/audit-log') {
    warm(queries.auditEvents());
    warm(queries.projects());
  } else {
    const project = path.match(/^\/app\/projects\/([0-9a-f]{8}-[0-9a-f-]{27})(?:\/([a-z-]+))?$/i);
    if (project) void warmProject(project[1]!, project[2], url.searchParams.get('env'));
  }
}

function saveData(): boolean {
  return Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);
}

/** Mounted once by the dashboard: any link or `data-prefetch` element warms its destination on intent. */
export function usePrefetchOnIntent(): void {
  useEffect(() => {
    if (saveData()) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pending: string | null = null;

    const destination = (event: Event): string | null => {
      const element = (event.target as Element | null)?.closest?.('[data-prefetch], a[href]');
      const href = element?.getAttribute('data-prefetch') ?? element?.getAttribute('href') ?? null;
      return href && href.startsWith('/app') ? href : null;
    };
    const cancel = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      pending = null;
    };
    // Hover and focus wait a moment, so sweeping the pointer across a row of
    // links does not warm every one of them.
    const onIntent = (event: Event) => {
      const href = destination(event);
      if (!href || href === pending) return;
      cancel();
      pending = href;
      timer = setTimeout(() => {
        timer = null;
        pending = null;
        prefetchPath(href);
      }, INTENT_DELAY);
    };
    // A touch is a decision, not a wander.
    const onTouch = (event: Event) => {
      const href = destination(event);
      if (href) prefetchPath(href);
    };

    document.addEventListener('pointerover', onIntent);
    document.addEventListener('focusin', onIntent);
    document.addEventListener('pointerout', cancel);
    document.addEventListener('touchstart', onTouch, { passive: true });
    return () => {
      cancel();
      document.removeEventListener('pointerover', onIntent);
      document.removeEventListener('focusin', onIntent);
      document.removeEventListener('pointerout', cancel);
      document.removeEventListener('touchstart', onTouch);
    };
  }, []);
}
